import { Component, OnInit, Input } from "@angular/core"
import { Store, Action } from "@ngrx/store"
import { AppState } from "@store/app-state"
import { TutorAllocationsStore } from "@store/tutor-allocations";
import { WorkshopsStore } from "@store/workshops";
import { ConstantsStore } from "@store/constants";
import { MatrixClassesStore } from "@store/matrix-classes";
import { TutorAllocationEffects } from "@reducers/effects/tutor-allocation";
import { TutorAllocation } from "@models/tutor-allocation";
import { Course } from "@models/course";
import { Workshop } from "@models/workshop";
import { Window } from "@models/window";
import { Campus } from "@models/campus";
import { Contact } from "@models/contact";
import { Observable } from "rxjs/Observable";
import { CompleterData, CompleterService, CompleterItem } from "ng2-completer";
import {
  CREATE_TUTOR_ALLOCATION,
  ADD_TUTOR_ALLOCATION,
  REPLACE_TUTOR_ALLOCATION,
  UPDATE_TUTOR_ALLOCATION
} from "@reducers/tutor-allocations";
import {
  FETCH_TEACHERS
} from "@reducers/matrix-class";
import { ModalService } from "@services/modal";

interface WindowsByCampus {
  campus: Campus,
  windows: Window[]
}

@Component({
  selector: 'tutor-allocation-form',
  templateUrl: './tutor-allocation-form.pug',
})
export class TutorAllocationFormComponent implements OnInit {

  @Input() public isEditing: boolean = false
  public errors: any ={}

  public tutorAllocationsStore: Observable<TutorAllocationsStore>
  public workshopsStore: Observable<WorkshopsStore>
  public constantsStore: Observable<ConstantsStore>
  public matrixClassesStore: Observable<MatrixClassesStore>

  public courses: Course[] = []
  public workshops: Workshop[] = []
  public tutors: Contact[] = []
  public ta: TutorAllocation
  public potentialWindows: Window[] = []

  public tutorSearchStr: string
  public tutorCompleter: CompleterData

  public courseSearchStr: string
  public courseCompleter: CompleterData

  constructor(
    private store: Store<AppState>,
    private completerService: CompleterService,
    private tutorAllocationEffects: TutorAllocationEffects,
    private modalService: ModalService,
  ) {}

  ngOnInit() {
    this.tutorAllocationsStore  = this.store.select("tutorAllocationsReducer")
    this.workshopsStore         = this.store.select("workshopsReducer")
    this.constantsStore         = this.store.select("constantsReducer")
    this.matrixClassesStore     = this.store.select("matrixClassesReducer")

    this.constantsStore.subscribe((s: ConstantsStore) => {
      this.courses = s.constants.courses
    })

    this.workshopsStore.subscribe((s: WorkshopsStore) => {
      this.workshops = s.workshops
    })

    this.matrixClassesStore.subscribe((s: MatrixClassesStore) => {
      this.tutors = s.teachers

      this.tutorCompleter =
        this.completerService.local(this.tutors, "fullName", "fullName")
    })


    this.tutorAllocationsStore.subscribe((s: TutorAllocationsStore) => {
      this.ta = s.activeTutorAllocation
      this.setupTutorAllocation()
      this.refreshPotentialWindows()
    })

    this.populateTeachers()

    this.courseCompleter =
      this.completerService.local(this.courses, "code, name", "name")
  }

  selectedTutor(e: CompleterItem) {
    if (e && e.originalObject) {
      const tutor = (e.originalObject as Contact)
      this.ta.contact = tutor
      this.ta.contactId = tutor.id
    }
    else {
      this.ta.contact = null
      this.ta.contactId = null
    }
  }

  selectedCourse(e: CompleterItem) {
    if (e && e.originalObject) {
      const course = e.originalObject as Course

      if (!this.ta.courseIds.find((id: number) => id == course.id)) {
        this.ta.courses.push(course)
        this.ta.courseIds.push(course.id)

        this.refreshPotentialWindows()
      }
    }
  }

  setupTutorAllocation() {
    if (!this.ta.courseIds) { this.ta.courseIds = [] }
    if (!this.ta.windowIds) { this.ta.windowIds = [] }
  }

  populateTeachers() {
    this.store.dispatch({ type: FETCH_TEACHERS })
  }

  refreshPotentialWindows() {
    let isPresent: boolean = false
    const windows: Window[] = []

    this.potentialWindows = []

    this.workshops.forEach((w: Workshop) => {
      isPresent = !!w.courseIds.find((cId: number) => {
        return this.ta.courseIds.indexOf(cId) >= 0
      })
      
      if (isPresent) {
        w.windows.forEach((w: Window) => {
          this.potentialWindows.push(w)
        })
      }
    })
  }

  getPotentialWindowsByCampus(): WindowsByCampus[] {
    const result: WindowsByCampus[] = []

    this.potentialWindows.forEach((w: Window) => {
      const isPresent = result.find(wbc => wbc.campus.id == w.campus.id)

      if (!isPresent) {
        const wbc: WindowsByCampus = { campus: w.campus, windows: [] }

        result.push(wbc)
      }
    })

    this.potentialWindows.forEach((w: Window) => {
      const wbc = result.find(wbc => wbc.campus.id == w.campus.id)
      wbc.windows.push(w)
    })

    return result
  }

  getWorkshopForWindow(window: Window): Workshop {
    return this.workshops.find((w: Workshop) => {
      return w.id == window.workshopId
    })
  }

  isWindowInAllocation(window: Window): boolean {
    return !!this.ta.windowIds.find((wId: number) => {
      return wId == window.id
    })
  }

  clickedWindowCheckbox(e: any, window: Window) {
    if (e.target.checked && !this.isWindowInAllocation(window)) {
      this.ta.windows.push(window)
      this.ta.windowIds.push(window.id)
    }
    else {
      this.ta.windows = this.ta.windows.filter((w: Window) => {
        return w.id !== window.id
      })
      this.ta.windowIds = this.ta.windowIds.filter((wId: number) => {
        return wId !== window.id
      })
    }
  }

  clickedRemoveCourse(course: Course) {
    this.ta.courses     = this.ta.courses.filter((c: Course) => c.id != course.id)
    this.ta.courseIds   = this.ta.courseIds.filter((id: number) => id != course.id)

    this.refreshPotentialWindows()
  }

  clickedSave() {
    if (!this.validateTutorAllocation()) { return }

    if (this.isEditing) {
      this.tutorAllocationEffects.update.take(1).subscribe((a: Action) => {
        if (a.type == REPLACE_TUTOR_ALLOCATION) {
          this.modalService.closeModal()
        }
      })
      this.store.dispatch({ type: UPDATE_TUTOR_ALLOCATION, payload: this.ta })
    }
    else {
      this.tutorAllocationEffects.create.take(1).subscribe((a: Action) => {
        if (a.type == ADD_TUTOR_ALLOCATION) {
          this.modalService.closeModal()
        }
      })

      this.store.dispatch({ type: CREATE_TUTOR_ALLOCATION, payload: this.ta })
    }
  }

  validateTutorAllocation() {
    let result = true
    this.errors = {}

    if (!this.ta.contactId) {
      result = false
      this.errors.contact = { required: true }
    }

    return result
  }

}



// WEBPACK FOOTER //
// /Users/jason/src/matrix/client/src/app/components/tutor-allocation-form.ts